import { Controller } from '@hotwired/stimulus';

/**
 * Dropdown Controller
 *
 * Renders dropdown menus via a portal appended to document.body to escape
 * overflow clipping from ancestor scroll containers (e.g. tables with
 * overflow-x-auto). Mirrors the portal pattern used in tooltip_controller.js.
 * Exception: a trigger inside the sidebar opened as a modal dialog (tablet
 * overlay, phone « Menu » sheet: `aria-modal="true"` on the `<aside>`) gets its
 * portal INSIDE that dialog, where screen readers can still reach it (`portalHost`).
 * Opt-in: any other dialog may carry `data-dropdown-portal-host` on its root
 * (`MeetingReports::ShareModalComponent`); a trigger under it portals INTO that
 * root, and Escape in the open menu closes the menu and stops there, so the host
 * dialog only closes on the next Escape. Without the attribute nothing changes.
 *
 * Copy items (`data-dropdown-copy-text`, `with_item(copy_text:)`) copy their text
 * synchronously inside the portal click (Safari keeps the user gesture), then
 * dispatch `clipboard:copied` / `clipboard:failed` (bubbling, `detail.text`) on the
 * ORIGINAL item; the original is not re-fired.
 *
 * `http_method` items (`<span role="menuitem">` inside a `button_to`) have no
 * native keyboard activation: Enter / Space click the focused span, and the
 * portal click handler submits the ORIGINAL form once (never the clone).
 *
 * Targets:
 * - menu: Hidden template element containing menu items (stays in DOM as source)
 * - trigger: The button that toggles the dropdown
 *
 * Values:
 * - align: `end` (default) right-aligns the menu to the trigger (opens leftwards);
 *   `start` left-aligns it (opens rightwards). Each flips only when the menu does
 *   not fit, then clamps to the viewport (`menuLeft`).
 * - anchor: optional element id; a menu opening downwards sits under that
 *   element when it is lower than the trigger (never covers it).
 * - side: `bottom` (default) opens under the trigger (above it when there is no
 *   room below). `auto` (`Ui::UserMenuComponent side: :auto`, the sidebar profile)
 *   opens to the RIGHT of the trigger when, at opening time, the trigger sits in
 *   the sidebar RAIL (`[data-sidebar-rail]`, written by
 *   `sidebar_collapse_controller.js`), `bottom` otherwise (`menuPositionSide`).
 *
 * Actions:
 * - toggle: Opens or closes the dropdown
 * - onTriggerKeydown: Handles keyboard open from trigger
 *
 * Disabled items (`aria-disabled="true"`, B04-08) stay reachable with the arrow
 * keys but a click, Enter or Space on them neither closes the menu nor
 * re-dispatches to the original item.
 *
 * Activating an enabled item hands the focus back to the trigger before the
 * original item is re-fired, so a drawer or modal it opens records the trigger as
 * its opener (locked by `dropdown_item_drawer_focus_return.test.mjs`). The portal
 * holds INERT copies (`inertClone`: no `data-controller`, `data-action` nor `on*`),
 * so only the original acts, once.
 *
 * @example
 * <div data-controller="dropdown">
 *   <button data-action="click->dropdown#toggle keydown->dropdown#onTriggerKeydown"
 *           data-dropdown-target="trigger"
 *           aria-haspopup="true" aria-expanded="false" aria-controls="menu-1">
 *     Menu
 *   </button>
 *   <div id="menu-1" data-dropdown-target="menu" role="menu" class="hidden ...">
 *     <a role="menuitem" tabindex="-1">Item</a>
 *   </div>
 * </div>
 */
// Item désactivé (B04-08) : `aria-disabled="true"`, seul repère (aucun `data-*`).
// Les flèches peuvent s'y poser (motif APG « menu ») ; clic, Entrée et Espace l'ignorent.
function isDisabledItem(node) {
  return node?.getAttribute?.('aria-disabled') === 'true';
}

// Ce qui ferait AGIR un clone du portail : un contrôleur Stimulus posé sur l'item
// lui-même (`data-controller="slide-over-trigger"` des menus du planning), son
// `data-action`, et les gestionnaires en ligne (`onclick="openModal(…)"`).
const CLONE_BEHAVIOUR_ATTRIBUTE = /^(data-controller|data-action|on.+)$/i;

function stripBehaviour(element) {
  element
    .getAttributeNames()
    .filter((name) => CLONE_BEHAVIOUR_ATTRIBUTE.test(name))
    .forEach((name) => element.removeAttribute(name));
  Array.from(element.children).forEach(stripBehaviour);
}

/**
 * Copie INERTE d'une entrée du menu, pour le portail (NR1-05).
 *
 * Le portail vit sous `document.body`, que Stimulus observe en entier : un item qui
 * porte son propre `data-controller` y reçoit une instance CONNECTÉE, et son
 * `data-action` se déclenche sur le clic du clone, en phase cible, AVANT le
 * `portalClickHandler` (phase de bouillonnement). Un `onclick` en ligne, recopié
 * par `cloneNode`, aussi. L'item agissait donc DEUX fois (clone, puis original
 * re-déclenché) et, la première fois, le tiroir ou la modale mémorisait le CLONE
 * focalisé comme déclencheur : un nœud que `close()` détache aussitôt, sans clé
 * d'équivalence, d'où un focus rendu à `<body>` à la fermeture. Mesuré dans
 * Chromium : `previouslyFocused` = l'item du portail détaché, deux `slide-over:open`
 * et deux chargements du formulaire par geste, deux `openModal` sur un menu
 * ordinaire. Le clone n'est qu'à regarder ; seul l'original agit, après que le
 * focus est rendu au déclencheur.
 *
 * @param {Element} node - une entrée de `menuTarget`
 * @returns {Element} sa copie profonde, sans contrôleur, action ni `on*`
 */
export function inertClone(node) {
  const copy = node.cloneNode(true);
  stripBehaviour(copy);
  return copy;
}

/**
 * Bord gauche du menu porté (pur, testé sous `node --test`).
 *
 * `align` = `'end'` (défaut, `data-dropdown-align-value` absent) : bord DROIT du
 * menu sur celui du déclencheur (ouverture vers la gauche), sinon bord gauche
 * sur bord gauche. `'start'` (FU-I, B04-01, le « + » d'une ligne du Gantt) :
 * l'inverse, ouverture vers la DROITE, bascule à gauche seulement faute de
 * place. Dans les deux cas, en dernier recours, le menu est calé dans l'écran.
 *
 * @param {{triggerRect: {left: number, right: number}, menuWidth: number,
 *   viewportWidth: number, viewportMargin: number, align?: string}} geometry
 * @returns {number}
 */
export function menuLeft({ triggerRect, menuWidth, viewportWidth, viewportMargin, align = 'end' }) {
  const startFits = triggerRect.left + menuWidth <= viewportWidth - viewportMargin;
  const endFits = triggerRect.right - menuWidth >= viewportMargin;
  const order =
    align === 'start'
      ? [
          [startFits, triggerRect.left],
          [endFits, triggerRect.right - menuWidth],
        ]
      : [
          [endFits, triggerRect.right - menuWidth],
          [startFits, triggerRect.left],
        ];
  const fit = order.find(([fits]) => fits);
  if (fit) return fit[1];
  return Math.max(viewportMargin, viewportWidth - menuWidth - viewportMargin);
}

// Posé sur l'`<aside>` du menu latéral en forme rail par `sidebar_collapse_controller.js`
// (`RAIL_ATTRIBUTE`), relu à chaque ouverture.
const SIDEBAR_RAIL_SELECTOR = '[data-sidebar-rail]';

/**
 * Côté d'ouverture du menu porté (pur, testé sous `node --test`).
 *
 * `side` = `'bottom'` (défaut, valeur absente) : sous le déclencheur, comme avant.
 * `'auto'` (G-19, profil du pied du menu latéral) : à DROITE quand le déclencheur est
 * dans le rail au moment de l'ouverture (64 px de large : sous lui, le menu
 * recouvrirait le rail et sortirait de l'écran par le bas), sous lui sinon. Jamais une
 * valeur du serveur : la forme du menu change sans rechargement.
 *
 * @param {{side?: string, inRail?: boolean}} options
 * @returns {'bottom'|'right'}
 */
export function menuPositionSide({ side = 'bottom', inRail = false } = {}) {
  return side === 'auto' && inRail ? 'right' : 'bottom';
}

/**
 * Position d'un menu ouvert à DROITE du déclencheur (pur) : bord gauche à `gap` du
 * bord droit du rail (`edgeRight`, sinon celui du déclencheur : le menu ne recouvre
 * pas le rail), bord bas aligné sur celui du déclencheur (le profil est en bas du
 * rail : le menu monte), calé dans la hauteur de l'écran. `null` quand la largeur ne
 * tient pas : l'appelant retombe sur l'ouverture vers le bas.
 *
 * @param {{triggerRect: {right: number, bottom: number}, edgeRight?: number,
 *   menuWidth: number, menuHeight: number, viewportWidth: number,
 *   viewportHeight: number, viewportMargin: number, gap: number}} geometry
 * @returns {{top: number, left: number}|null}
 */
export function menuRightPlacement({
  triggerRect,
  edgeRight = -Infinity,
  menuWidth,
  menuHeight,
  viewportWidth,
  viewportHeight,
  viewportMargin,
  gap,
}) {
  const left = Math.max(triggerRect.right, edgeRight) + gap;
  if (left + menuWidth > viewportWidth - viewportMargin) return null;

  const lowest = viewportHeight - menuHeight - viewportMargin;
  const top = Math.max(viewportMargin, Math.min(triggerRect.bottom - menuHeight, lowest));
  return { top, left };
}

// Le menu latéral ouvert en DIALOGUE MODAL : superposition tablette (`sidebar_collapse_controller.js`) et
// feuille « Menu » du téléphone (`sidebar_controller.js`) posent `aria-modal="true"` sur l'`<aside>`
// `data-sidebar-target="sidebar"`. Lu à l'ouverture, jamais mémorisé. Volontairement PAS tout
// `[aria-modal="true"]` : d'autres menus vivent dans des dialogues modaux (`Ui::InvoicePreviewModalComponent`,
// le tiroir du lot du planning `phase-panel`), hors du périmètre de la revue T1.8 ; ils restent sous `body`.
const SIDEBAR_DIALOG_SELECTOR = '[data-sidebar-target="sidebar"][aria-modal="true"]';

// Hôte de portail OPT-IN (refonte de la modale « Diffusion du CR ») : un dialogue modal qui veut garder
// ses menus joignables pose `data-dropdown-portal-host` sur sa racine
// (`MeetingReports::ShareModalComponent`). Sans l'attribut, rien ne change pour les autres dialogues.
const PORTAL_HOST_ATTRIBUTE = 'data-dropdown-portal-host';
const PORTAL_HOST_SELECTOR = `[${PORTAL_HOST_ATTRIBUTE}]`;

/**
 * Hôte du portail (pur, testé sous `node --test`) : le menu latéral ouvert en dialogue modal qui contient
 * le déclencheur, sinon l'ancêtre opt-in `[data-dropdown-portal-host]`, sinon `body`. Hors d'un
 * `aria-modal="true"`, le contenu est inerte pour les lecteurs d'écran : sous `<body>`, le menu du profil
 * ouvert depuis la feuille « Menu » devenait injoignable (revue T1.8). Pour tout autre déclencheur,
 * `body`, comme avant.
 *
 * @param {{closest?: Function}|null} trigger
 * @param {Element} body
 * @returns {Element}
 */
export function portalHost(trigger, body) {
  return (
    trigger?.closest?.(SIDEBAR_DIALOG_SELECTOR) || trigger?.closest?.(PORTAL_HOST_SELECTOR) || body
  );
}

// Le menu est-il porté dans un hôte opt-in ? Seul cas où Échap s'arrête au menu (la modale hôte ne se
// ferme pas sur le même Échap) ; partout ailleurs, Échap se propage comme avant.
function isOptInHost(host) {
  return Boolean(host?.hasAttribute?.(PORTAL_HOST_ATTRIBUTE));
}

// Item « Copier » (`with_item(copy_text:)`) : le texte à copier, `null` pour tout autre item.
function copyTextOf(node) {
  return node?.getAttribute?.('data-dropdown-copy-text') ?? null;
}

// Balises que le navigateur active d'elles-mêmes à Entrée (clic synthétisé sur l'élément focalisé).
const NATIVELY_ACTIVATED_TAGS = new Set(['BUTTON', 'A']);

// Item `http_method` (`button_to` : `<button type="submit"><span role="menuitem">`) : le `<span>`
// focalisé n'a aucune activation native, Entrée / Espace ne faisaient rien.
function needsKeyboardActivation(node) {
  if (!node || NATIVELY_ACTIVATED_TAGS.has(node.tagName)) return false;
  return Boolean(node.closest?.('form'));
}

export default class extends Controller {
  static targets = ['menu', 'trigger'];
  // `start` : le menu s'ouvre vers la droite (`Ui::DropdownMenuComponent` `align: :start`).
  // `anchor` : id d'un élément sous lequel le menu s'ouvre (`Ui::DropdownMenuComponent` `anchor_id:`).
  // `side` : `bottom` (défaut) ou `auto` (`Ui::UserMenuComponent` `side:`), voir `menuPositionSide`.
  static values = {
    align: { type: String, default: 'end' },
    anchor: String,
    side: { type: String, default: 'bottom' },
  };

  toggle(event) {
    event.stopPropagation();
    if (this.portalElement) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    document.dispatchEvent(
      new CustomEvent('dropdown:opened', { detail: { source: this.element } })
    );

    this.createPortal();
    this.positionPortal();
    this.updateAriaExpanded(true);

    if (this.hasTriggerTarget) {
      this.triggerTarget.setAttribute('aria-controls', this.portalMenuId);
    }

    const firstItem = this.portalElement.querySelector('[role="menuitem"], [role="option"]');
    if (firstItem) firstItem.focus();

    this.portalKeydownHandler = this.onPortalKeydown.bind(this);
    this.portalElement.addEventListener('keydown', this.portalKeydownHandler);

    this.portalClickHandler = (e) => {
      const portalItem = e.target.closest('[role="menuitem"], [role="option"]');
      if (!portalItem) return;

      // B04-08 : un item DÉSACTIVÉ (`aria-disabled="true"`, rendu par
      // `Ui::DropdownMenuComponent#with_rich_item(disabled: true)`) ne fait rien :
      // ni fermeture, ni re-déclenchement de l'original. Il reste un item (même
      // rôle), donc l'index portail ↔ original des voisins reste aligné.
      if (isDisabledItem(portalItem)) {
        e.preventDefault();
        return;
      }

      // The CLONE must never act on its own — it exists only to be looked at.
      // close() detaches it during the bubble phase, but the event path was
      // already computed, so Turbo's LinkClickObserver (bound on
      // documentElement) still sees the clone and acts on it. For an
      // href-bearing item that means Turbo fires a request from the clone AND
      // the re-dispatch below fires one from the original: TWO requests for one
      // gesture. On a GET item the two Visits coalesce and nothing shows; on
      // `<a data-turbo-method="post">` it is a duplicate WRITE — measured on the
      // CR cover photo « Reprendre celle du chantier »: two `added` ActivityLog
      // rows 7 ms apart, two blobs uploaded, the first left to purge_later.
      // Planning hit the same race from the other side and worked around it by
      // dropping `href` entirely (`planning/plan_phases/_add_menu.html.erb:5-8`);
      // that is not available to every caller, so the guard belongs here.
      e.preventDefault();

      // Portal clones live OUTSIDE the originating Stimulus scope, and
      // `inertClone` strips their own controllers, actions and `on*` handlers,
      // so nothing on the clone dispatches. Re-fire the click on
      // the matching ORIGINAL item (still inside menuTarget) so Stimulus
      // and native handlers (href navigation, button_to submission) fire
      // normally. Matched by positional index — the portal items mirror
      // menuTarget.children order 1:1 (cloneNode preserves order). The
      // template MUST place [role="menuitem"]/[role="option"] on the
      // outer element of each entry and MUST NOT conditionally hide
      // entries inside the loop (would desync portal vs. original index).
      //
      // Re-dispatched clicks bypass Rails UJS data-confirm chains (UJS
      // intercepts at the form/anchor level, which we don't traverse).
      // If a rich-item dropdown ever needs confirmation, use
      // data-turbo-confirm on the form instead.
      const portalItems = Array.from(
        this.portalElement.querySelectorAll('[role="menuitem"], [role="option"]')
      );
      const originalItems = Array.from(
        this.menuTarget.querySelectorAll('[role="menuitem"], [role="option"]')
      );
      const idx = portalItems.indexOf(portalItem);
      const original = idx >= 0 ? originalItems[idx] : null;

      // Item « Copier » (`data-dropdown-copy-text`) : la copie a lieu ICI, pendant le clic, jamais
      // dans la frame suivante (Safari exige le geste de l'utilisateur). Le résultat est annoncé sur
      // l'item ORIGINAL, en bouillonnement (`share-receipt` l'écoute sur un ancêtre). La copie EST
      // l'action de l'item : l'original n'est pas re-déclenché.
      const copyText = copyTextOf(original);
      if (copyText !== null) {
        this.close();
        if (this.hasTriggerTarget) this.triggerTarget.focus();
        this.#copy(original, copyText);
        return;
      }

      this.close();

      // Focus back on the trigger BEFORE the item acts (APG menu button). close()
      // just removed the portal, which held the focus: without this it falls to
      // <body>, and a drawer or modal opened by the item records <body> as its
      // opener, so closing it restores nothing (QA H1 DS-6: Gantt row « + » ›
      // « Ajouter un sous-lot » › « Annuler » left the focus on <body>). The item's
      // own action runs next frame and may move the focus elsewhere.
      if (this.hasTriggerTarget) this.triggerTarget.focus();

      // Defer dispatch to next frame so close() finishes teardown (removes
      // listeners, clears the portal element) before we trigger a navigation
      // or Stimulus action that may mutate the DOM the dropdown lived in.
      if (original) {
        requestAnimationFrame(() => {
          // For button_to-wrapped items, .click() on the inner span doesn't
          // submit the form. Detect the wrapping <form> and submit via the
          // actual submit control (preserves data-turbo-confirm semantics).
          // Currently no rich-item caller uses button_to, but the plain Item
          // branch's button_to path lands inside a <form>, so this is the
          // defensive fallback for plain menu items reached via this same
          // re-dispatch path.
          const form = original.closest('form');
          if (form && original.tagName !== 'BUTTON' && original.tagName !== 'A') {
            const submit = form.querySelector('button[type="submit"], input[type="submit"]');
            if (submit) submit.click();
            else form.requestSubmit();
            return;
          }
          original.click();
        });
      }
    };
    this.portalElement.addEventListener('click', this.portalClickHandler);

    this.resizeCloseHandler = () => this.close();
    window.addEventListener('resize', this.resizeCloseHandler);

    // Delay scroll-close listener to avoid false positives from layout shifts
    // (e.g. flash message auto-dismiss animation or focus-triggered scrolling)
    this.scrollCloseTimer = setTimeout(() => {
      this.scrollCloseHandler = (e) => {
        if (!this.portalElement?.contains(e.target)) this.close();
      };
      window.addEventListener('scroll', this.scrollCloseHandler, { capture: true, passive: true });
    }, 150);
  }

  close() {
    if (this.portalElement) {
      this.portalElement.removeEventListener('keydown', this.portalKeydownHandler);
      this.portalElement.removeEventListener('click', this.portalClickHandler);
      this.portalElement.remove();
      this.portalElement = null;
    }

    if (this.scrollCloseTimer) {
      clearTimeout(this.scrollCloseTimer);
      this.scrollCloseTimer = null;
    }
    window.removeEventListener('scroll', this.scrollCloseHandler, { capture: true });
    window.removeEventListener('resize', this.resizeCloseHandler);

    if (this.hasTriggerTarget) {
      this.triggerTarget.setAttribute('aria-controls', this.menuTarget.id);
    }

    this.updateAriaExpanded(false);
  }

  hide(event) {
    if (!this.element.contains(event.target) && !this.portalElement?.contains(event.target)) {
      this.close();
    }
  }

  onTriggerKeydown(event) {
    if (['ArrowDown', 'Enter', ' '].includes(event.key)) {
      event.preventDefault();
      this.open();
    }
  }

  onPortalKeydown(event) {
    const items = Array.from(
      this.portalElement.querySelectorAll('[role="menuitem"], [role="option"]')
    );
    const currentIndex = items.indexOf(document.activeElement);

    switch (event.key) {
      // B04-08 : Entrée / Espace sur un item désactivé. Le navigateur synthétiserait
      // un clic sur le `<button>` focalisé ; `preventDefault()` l'en empêche et le
      // menu reste ouvert. Sur un item actif, le clic natif suit son cours.
      // Item `http_method` focalisé (un `<span>` dans le `<button>` d'un `button_to`) : aucun clic
      // natif, on clique le `<span>` nous-mêmes ; `portalClickHandler` annule alors la soumission du
      // clone et soumet le formulaire ORIGINAL, une fois.
      case 'Enter':
      case ' ':
        if (isDisabledItem(document.activeElement)) {
          event.preventDefault();
        } else if (needsKeyboardActivation(document.activeElement)) {
          event.preventDefault();
          document.activeElement.click();
        }
        break;
      // Dans un hôte opt-in (`data-dropdown-portal-host`), Échap s'arrête au menu : la modale hôte
      // ne se ferme qu'au suivant. Ailleurs, propagation inchangée.
      case 'Escape': {
        const stops = isOptInHost(this.portalHostElement);
        event.preventDefault();
        this.close();
        if (this.hasTriggerTarget) this.triggerTarget.focus();
        if (stops) event.stopPropagation();
        break;
      }
      case 'ArrowDown':
        event.preventDefault();
        this.focusItem(items, currentIndex + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.focusItem(items, currentIndex - 1);
        break;
      case 'Home':
        event.preventDefault();
        this.focusItem(items, 0);
        break;
      case 'End':
        event.preventDefault();
        this.focusItem(items, items.length - 1);
        break;
      case 'Tab':
        this.close();
        break;
    }
  }

  // Appelé SYNCHRONEMENT depuis le clic du portail (geste conservé). Mêmes noms d'événements que
  // `clipboard_controller.js` (`clipboard:copied` / `clipboard:failed`) : un seul écouteur côté hôte.
  #copy(item, text) {
    const announce = (type, detail) =>
      item.dispatchEvent(new CustomEvent(type, { bubbles: true, detail }));
    let pending;
    try {
      pending = navigator.clipboard.writeText(text);
    } catch (error) {
      pending = Promise.reject(error);
    }
    pending.then(
      () => announce('clipboard:copied', { text }),
      () => announce('clipboard:failed', { text })
    );
  }

  focusItem(items, index) {
    if (items.length === 0) return;
    const clampedIndex = Math.max(0, Math.min(index, items.length - 1));
    items[clampedIndex].focus();
  }

  // Portal element structure: outer fixed wrapper (width: auto, no
  // content) > single absolute cloned menu child (carries explicit
  // width class). positionPortal() depends on firstElementChild being
  // the cloned menu — do not append sibling decorations here.
  createPortal() {
    this.portalMenuId = `${this.menuTarget.id}-portal`;

    this.portalElement = document.createElement('div');
    this.portalElement.className = 'fixed z-50';
    this.portalElement.setAttribute('data-dropdown-portal', '');

    const menu = document.createElement('div');
    menu.id = this.portalMenuId;
    menu.setAttribute('role', this.menuTarget.getAttribute('role') || 'menu');
    menu.setAttribute('aria-label', this.menuTarget.getAttribute('aria-label') || '');
    // Strip the source template's in-flow positioning hints. The original menu
    // lives inside `relative` parent and uses `absolute right-0/left-0 mt-2`
    // to anchor itself in-place. Inside the portal wrapper those classes
    // fight positionPortal()'s computed top/left: the wrapper has width 0
    // (its only child is absolute), so `right-0` pulls the cloned menu's
    // right edge to wrapper.left, painting the menu one full menuWidth to
    // the left of where positionPortal() intended. `mt-2` adds another 8px
    // below the gap already applied in positionPortal(). Strip all four so
    // the wrapper's top/left is the single source of truth.
    menu.className = this.menuTarget.className
      .replace('hidden', '')
      .replace(/\babsolute\b/, '')
      .replace(/\bright-0\b/, '')
      .replace(/\bleft-0\b/, '')
      .replace(/\bmt-2\b/, '')
      .replace(/\s+/g, ' ')
      .trim();

    // Copies INERTES (`inertClone`) : un clone qui agirait ouvrirait le tiroir en se
    // mémorisant comme déclencheur, avant le re-déclenchement de l'original.
    Array.from(this.menuTarget.children).forEach((child) => {
      menu.appendChild(inertClone(child));
    });

    this.portalElement.appendChild(menu);
    this.portalHostElement = portalHost(
      this.hasTriggerTarget ? this.triggerTarget : this.element,
      document.body
    );
    this.portalHostElement.appendChild(this.portalElement);
  }

  // Origine du bloc conteneur du portail `fixed`. Sous `<body>` : l'écran, (0, 0), sans mesure (tous les
  // appelants hors dialogue). Dans un dialogue transformé (l'`<aside>` porte `translate`), `fixed` se
  // rapporte au dialogue : on mesure le portail posé en (0, 0) pour viser l'écran malgré tout.
  #containingOrigin() {
    const underBody = !this.portalHostElement || this.portalHostElement === document.body;
    if (underBody) return { top: 0, left: 0 };

    this.portalElement.style.top = '0px';
    this.portalElement.style.left = '0px';
    const rect = this.portalElement.getBoundingClientRect();
    return { top: rect.top, left: rect.left };
  }

  #place(top, left) {
    const origin = this.#containingOrigin();
    this.portalElement.style.top = `${top - origin.top}px`;
    this.portalElement.style.left = `${left - origin.left}px`;
    this.portalElement.style.visibility = '';
  }

  positionPortal() {
    const triggerRect = this.triggerTarget.getBoundingClientRect();
    const gap = 4;
    const viewportMargin = 8;

    // Measure with visibility:hidden trick
    this.portalElement.style.visibility = 'hidden';

    // Measure the CLONED MENU element, not the portal wrapper. The wrapper
    // is `position: fixed` and its only child is `position: absolute`, so
    // the child is removed from the wrapper's flow and the wrapper has
    // zero intrinsic width. Reading the wrapper's getBoundingClientRect()
    // returns {width: 0, height: 0}, which makes the right-align branch
    // below (`triggerRect.right - menuWidth >= viewportMargin`) always
    // succeed with `left = triggerRect.right` — menu opens to the right
    // of the trigger instead of below it. createPortal appends exactly
    // one direct child (the cloned menu), so firstElementChild is safe.
    const menuElement = this.portalElement.firstElementChild || this.portalElement;
    const menuRect = menuElement.getBoundingClientRect();
    const menuWidth = menuRect.width;
    const menuHeight = menuRect.height;

    const railPlacement = this.#railPlacement({
      triggerRect,
      menuWidth,
      menuHeight,
      viewportMargin,
      gap,
    });
    if (railPlacement) {
      this.#place(railPlacement.top, railPlacement.left);
      return;
    }

    // Vertical positioning. `anchor` (FU-I, B04-06): opening downwards, the menu
    // clears the named element under the trigger too (the reason of a disabled
    // button, which it used to cover). Horizontal placement stays on the trigger.
    const below = this.#belowEdge(triggerRect);
    const spaceBelow = window.innerHeight - below - viewportMargin;
    const spaceAbove = triggerRect.top - viewportMargin;
    let top;

    if (spaceBelow >= menuHeight) {
      top = below + gap;
    } else if (spaceAbove >= menuHeight) {
      top = triggerRect.top - menuHeight - gap;
    } else {
      // Best fit: place where there's more space, clamped to viewport
      if (spaceBelow >= spaceAbove) {
        top = Math.min(below + gap, window.innerHeight - menuHeight - viewportMargin);
      } else {
        top = Math.max(triggerRect.top - menuHeight - gap, viewportMargin);
      }
    }

    const left = menuLeft({
      triggerRect,
      menuWidth,
      viewportWidth: window.innerWidth,
      viewportMargin,
      align: this.alignValue,
    });

    this.#place(top, left);
  }

  // `side: auto` (G-19) : à droite du déclencheur quand il est dans le rail, lu À
  // L'OUVERTURE. `null` hors du rail ou faute de largeur : ouverture vers le bas comme
  // les autres menus. Sans `side: auto` (tous les autres appelants), aucune lecture du
  // DOM en plus.
  #railPlacement({ triggerRect, menuWidth, menuHeight, viewportMargin, gap }) {
    const rail = this.sideValue === 'auto' ? this.element.closest(SIDEBAR_RAIL_SELECTOR) : null;
    const side = menuPositionSide({ side: this.sideValue, inRail: Boolean(rail) });
    if (side !== 'right') return null;

    return menuRightPlacement({
      triggerRect,
      edgeRight: rail.getBoundingClientRect().right,
      menuWidth,
      menuHeight,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      viewportMargin,
      gap,
    });
  }

  // The lower edge a downward menu opens under: the trigger's, or the anchor's
  // when `anchor` names an element of the page that sits lower (absent ⇒ trigger).
  #belowEdge(triggerRect) {
    const anchor = this.anchorValue ? document.getElementById(this.anchorValue) : null;
    if (!anchor) return triggerRect.bottom;
    return Math.max(triggerRect.bottom, anchor.getBoundingClientRect().bottom);
  }

  get isOpen() {
    return !!this.portalElement;
  }

  updateAriaExpanded(expanded) {
    if (this.hasTriggerTarget) {
      this.triggerTarget.setAttribute('aria-expanded', String(expanded));
    }
  }

  connect() {
    this.portalElement = null;

    this.hideHandler = this.hide.bind(this);
    document.addEventListener('click', this.hideHandler);

    this.closeHandler = () => this.close();
    document.addEventListener('turbo:before-visit', this.closeHandler);

    this.closeOthersHandler = (event) => {
      if (event.detail.source !== this.element) this.close();
    };
    document.addEventListener('dropdown:opened', this.closeOthersHandler);
  }

  disconnect() {
    this.close();
    document.removeEventListener('click', this.hideHandler);
    document.removeEventListener('turbo:before-visit', this.closeHandler);
    document.removeEventListener('dropdown:opened', this.closeOthersHandler);
  }
}
